import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  AlertTriangle,
  Receipt,
  BarChart3,
  Users,
  Settings,
  ChevronLeft,
  Store,
} from 'lucide-react';

const menuGroups = [
  {
    title: 'MAIN',
    items: [
      { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    ]
  },
  {
    title: 'INVENTORY & CATALOG',
    items: [
      { path: '/products', label: 'Products', icon: Package },
      { path: '/low-inventory', label: 'Low Stock', icon: AlertTriangle },
    ]
  },
  {
    title: 'SALES & ANALYTICS',
    items: [
      { path: '/sales', label: 'Sales Orders', icon: Receipt },
      { path: '/reports', label: 'Reports & Analytics', icon: BarChart3 },
    ]
  },
  {
    title: 'ADMINISTRATION',
    items: [
      { path: '/users', label: 'User Management', icon: Users },
      { path: '/settings', label: 'Store Settings', icon: Settings },
    ]
  }
];

const Sidebar = ({ isCollapsed, setIsCollapsed, isMobileOpen, setIsMobileOpen }) => {
  return (
    <>
      {/* Mobile Backdrop */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/60 backdrop-blur-sm lg:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar Container */}
      <aside
        className={`
          fixed top-0 left-0 bottom-0 z-40 flex flex-col
          bg-white dark:bg-[#111827]
          border-r border-slate-200/90 dark:border-slate-800
          transition-all duration-300 ease-in-out
          ${isCollapsed ? 'w-20' : 'w-64'}
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          shadow-[4px_0_24px_0_rgba(0,0,0,0.02)] dark:shadow-[4px_0_24px_0_rgba(0,0,0,0.2)]
        `}
      >
        {/* Sidebar Header / Logo */}
        <div className="h-16 flex items-center justify-between px-5 border-b border-slate-100 dark:border-slate-800/80">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-emerald-600 to-teal-500 flex items-center justify-center text-white shadow-md shadow-emerald-500/20 shrink-0">
              <Store className="w-5 h-5" />
            </div>
            {!isCollapsed && (
              <div className="truncate">
                <span className="text-base font-bold text-slate-900 dark:text-white tracking-tight">
                  Swift<span className="text-emerald-500">Sale</span>
                </span>
              </div>
            )}
          </div>

          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="hidden lg:flex p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <ChevronLeft className={`w-4 h-4 transform transition-transform ${isCollapsed ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Sidebar Nav Links */}
        <div className="flex-1 overflow-y-auto py-5 px-3 space-y-6">
          {menuGroups.map((group, groupIdx) => (
            <div key={groupIdx}>
              {!isCollapsed && (
                <p className="px-3 mb-2 text-[10px] font-bold tracking-wider uppercase text-slate-400 dark:text-slate-500">
                  {group.title}
                </p>
              )}
              <div className="space-y-1">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsMobileOpen(false)}
                      title={isCollapsed ? item.label : undefined}
                      className={({ isActive }) => `
                        group flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-semibold
                        transition-all duration-150
                        ${isCollapsed ? 'justify-center' : ''}
                        ${isActive
                          ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400 shadow-sm shadow-emerald-500/5'
                          : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 hover:text-slate-900 dark:hover:bg-slate-800/60 dark:hover:text-white'
                        }
                      `}
                    >
                      {({ isActive }) => (
                        <>
                          <Icon
                            className={`w-4 h-4 shrink-0 transition-colors ${
                              isActive
                                ? 'text-emerald-600 dark:text-emerald-400'
                                : 'text-slate-400 group-hover:text-slate-700 dark:text-slate-400 dark:group-hover:text-slate-200'
                            }`}
                          />
                          {!isCollapsed && (
                            <span className="flex-1 truncate">{item.label}</span>
                          )}
                        </>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
